import { MOVEMENT_TYPES, type BalanceDto, type LotStatus, type MovementDto } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { LotDetailPanel } from '../components/LotDetailPanel';
import { ReceiveStockForm } from '../components/ReceiveStockForm';
import { StockActionPanel, type StockActionMode } from '../components/StockActionPanel';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { Segmented } from '../components/Segmented';
import { StickyActionBar } from '../components/StickyActionBar';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  InfoNote,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import {
  formatDate,
  formatQuantity,
  formatSignedQuantity,
  formatUnitCost,
  humanise,
} from '../lib/format';
import { useDeepLink } from '../lib/useDeepLink';
import { Can, useSession } from '../lib/session';

type Tab = 'balances' | 'ledger';

const STATUS_TONES: Record<LotStatus, 'green' | 'hazard' | 'accent' | 'muted'> = {
  available: 'green',
  quarantine: 'hazard',
  rejected: 'muted',
  consumed: 'muted',
  scrapped: 'muted',
};

export function InventoryPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canMoveStock = can('inventory.manage');

  const [tab, setTab] = useState<Tab>('balances');
  const [search, setSearch] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [movementType, setMovementType] = useState('');

  const [showReceive, setShowReceive] = useState(false);
  const [receiveItemId, setReceiveItemId] = useState<string | undefined>(undefined);
  const [pendingAction, setPendingAction] = useState<StockActionMode | null>(null);
  const [actionBalance, setActionBalance] = useState<BalanceDto | null>(null);
  const [openLotId, setOpenLotId] = useState<string | null>(null);
  const [reversing, setReversing] = useState<MovementDto | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  /**
   * The dashboard links here with a small set of one-shot parameters so a person
   * lands on the exact panel they meant: receiving a named item, an intent to
   * issue or transfer, a lot, a tab or a search term. Each is consumed and then
   * stripped from the URL (see `useDeepLink`).
   */
  useDeepLink('tab', (value) => setTab(value === 'ledger' ? 'ledger' : 'balances'));
  useDeepLink('search', (value) => setSearch(value));
  useDeepLink('lot', (value) => setOpenLotId(value));
  useDeepLink('item', (value) => setReceiveItemId(value));
  useDeepLink('action', (value) => {
    if (value === 'receive') {
      setShowReceive(true);
      setPendingAction(null);
      return;
    }
    if (value === 'issue' || value === 'transfer' || value === 'adjust' || value === 'scrap') {
      setPendingAction(value);
      setShowReceive(false);
      setTab('balances');
    }
  });

  const balances = useQuery({
    queryKey: ['inventory', 'balances', { search, hideEmpty }],
    queryFn: () => api.inventory.balances({ search, hideEmpty, limit: 100 }),
  });

  const movements = useQuery({
    queryKey: ['inventory', 'movements', { movementType }],
    queryFn: () =>
      api.inventory.movements({ ...(movementType ? { type: movementType } : {}), limit: 100 }),
  });

  const lowStock = useQuery({
    queryKey: ['inventory', 'low-stock'],
    queryFn: api.inventory.lowStock,
  });

  const reconcile = useMutation({ mutationFn: api.inventory.reconcile });

  const reverse = useMutation({
    mutationFn: (variables: { id: string; reason: string }) =>
      api.inventory.reverse(variables.id, variables.reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['lot'] });
      setReversing(null);
      setReverseReason('');
    },
  });

  /** Only one panel is open at a time, so it is always obvious what is being acted on. */
  function openReceive() {
    setShowReceive(true);
    setActionBalance(null);
    setOpenLotId(null);
    setPendingAction(null);
  }

  function openAction(balance: BalanceDto) {
    setActionBalance(balance);
    setShowReceive(false);
    setOpenLotId(null);
  }

  function openLot(lotId: string) {
    setOpenLotId(lotId);
    setShowReceive(false);
    setActionBalance(null);
  }

  const lowStockCount = lowStock.data?.items.length ?? 0;

  /**
   * One definition per table. The desktop order is what the table has always
   * shown; `primary`/`align` only decide how a row becomes a card on a phone.
   */
  const balanceColumns: DataColumn<BalanceDto>[] = [
    {
      key: 'item',
      header: 'Item',
      primary: true,
      cell: (balance) => (
        <>
          <span className="font-mono text-xs">{balance.itemCode}</span>
          <br />
          <span className="font-medium text-ink">{balance.itemName}</span>
        </>
      ),
    },
    {
      key: 'lot',
      header: 'Lot',
      cell: (balance) => (
        <button
          type="button"
          className="font-mono text-xs font-semibold text-brand-deep underline-offset-2 hover:underline"
          onClick={() => openLot(balance.lotId)}
        >
          {balance.lotCode}
        </button>
      ),
    },
    { key: 'location', header: 'Location', cell: (balance) => balance.locationCode },
    {
      key: 'onHand',
      header: 'On hand',
      align: 'right',
      cell: (balance) => (
        <span className="whitespace-nowrap font-semibold text-ink">
          {formatQuantity(balance.quantity, balance.baseUom)}
        </span>
      ),
    },
    {
      key: 'unitCost',
      header: 'Unit cost',
      cell: (balance) => (
        <span className="whitespace-nowrap">
          {formatUnitCost(balance.unitCost, balance.baseUom)}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      cell: (balance) => (
        <span className="whitespace-nowrap">{formatQuantity(balance.value)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (balance) => (
        <>
          <Badge tone={STATUS_TONES[balance.status]}>{humanise(balance.status)}</Badge>
          {balance.status !== 'available' ? (
            <span className="ml-1.5 align-middle">
              <Tooltip
                subject={humanise(balance.status)}
                text={
                  balance.status === 'quarantine'
                    ? 'On hold. The material is real, counted and valued, but no batch can draw on it until a quality check releases the lot.'
                    : 'Not usable. A rejected lot stays out of production; it has to be reopened for re-testing before it can be released again.'
                }
              />
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: 'expires',
      header: 'Expires',
      cell: (balance) => (
        <span className="whitespace-nowrap text-xs">{formatDate(balance.expiryDate)}</span>
      ),
    },
  ];

  const movementColumns: DataColumn<MovementDto>[] = [
    { key: 'when', header: 'When', cell: (movement) => (
      <span className="whitespace-nowrap text-xs">
        {movement.occurredAt.slice(0, 16).replace('T', ' ')}
      </span>
    ) },
    {
      key: 'type',
      header: 'Type',
      cell: (movement) => (
        <>
          {humanise(movement.type)}
          {movement.reversalOfId ? (
            <span className="ml-1 text-xs text-accent">(reversal)</span>
          ) : null}
        </>
      ),
    },
    {
      key: 'itemLot',
      header: 'Item / lot',
      primary: true,
      cell: (movement) => (
        <>
          <span className="font-mono text-xs">{movement.itemCode}</span>
          <br />
          <button
            type="button"
            className="font-mono text-xs text-brand-deep underline-offset-2 hover:underline"
            onClick={() => openLot(movement.lotId)}
          >
            {movement.lotCode}
          </button>
        </>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      align: 'right',
      cell: (movement) => (
        <span
          className={`whitespace-nowrap font-semibold ${
            movement.direction === 'out' ? 'text-red-700' : 'text-brand-deep'
          }`}
        >
          {formatSignedQuantity(movement.quantity, movement.uom)}
        </span>
      ),
    },
    { key: 'location', header: 'Location', cell: (movement) => movement.locationCode },
    { key: 'reason', header: 'Reason', cell: (movement) => (
      <span className="text-xs">{movement.reason ?? '—'}</span>
    ) },
    { key: 'by', header: 'By', cell: (movement) => (
      <span className="text-xs">{movement.actorName ?? 'system'}</span>
    ) },
  ];

  const balanceActions = canMoveStock
    ? (balance: BalanceDto) => (
        <Button tone="ghost" onClick={() => openAction(balance)}>
          Move / adjust
        </Button>
      )
    : undefined;

  const movementActions = canMoveStock
    ? (movement: MovementDto) =>
        movement.reversalOfId ? (
          <span className="text-xs text-ink-muted">—</span>
        ) : (
          <Button
            tone="ghost"
            onClick={() => {
              setReversing(movement);
              setReverseReason('');
              reverse.reset();
            }}
          >
            Reverse
          </Button>
        )
    : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Inventory"
        title="Stock on hand"
        description="On-hand quantities are never edited. Every change is a movement in an append-only ledger, and these balances are the running sum of it."
        actions={
          <Can capability="inventory.manage">
            <Button onClick={openReceive}>Receive stock</Button>
          </Can>
        }
      />

      {lowStockCount > 0 ? (
        <div className="mb-6 rounded-md border-l-4 border-hazard bg-white px-4 py-3 text-sm shadow-sm">
          <p className="font-bold uppercase tracking-wide text-ink">
            {lowStockCount} item{lowStockCount === 1 ? '' : 's'} at or below the reorder point
          </p>
          <ul className="mt-2 space-y-1">
            {lowStock.data?.items.slice(0, 6).map((row) => (
              <li key={row.itemId} className="text-ink-body">
                <span className="font-mono text-xs">{row.code}</span> — {row.name}:{' '}
                <strong>{formatQuantity(row.onHand, row.baseUom)}</strong> on hand, reorder at{' '}
                {formatQuantity(row.reorderPoint, row.baseUom)}
                {row.shortfall !== '0' ? (
                  <span className="text-ink-muted">
                    {' '}
                    (short by {formatQuantity(row.shortfall, row.baseUom)})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button
          tone="outline"
          onClick={() => reconcile.mutate()}
          disabled={reconcile.isPending}
        >
          {reconcile.isPending ? 'Checking…' : 'Check the ledger'}
        </Button>
        <span className="text-sm text-ink-muted">
          Re-sums every movement and compares it against the cached balances.
        </span>
      </div>

      {reconcile.data ? (
        <div className="mb-6">
          {reconcile.data.healthy ? (
            <InfoNote>
              <strong>Ledger intact.</strong> {reconcile.data.balanceRows} cached balance(s) across{' '}
              {reconcile.data.movementRows} movement(s) agree exactly.
            </InfoNote>
          ) : (
            <ErrorNote>
              <strong>Ledger drift detected.</strong>{' '}
              {reconcile.data.discrepancies.length} balance(s) disagree with their movements and{' '}
              {reconcile.data.orphanMovements.length} movement group(s) have no balance row. This
              should be impossible — investigate before trusting these numbers.
            </ErrorNote>
          )}
        </div>
      ) : null}

      {reconcile.error ? (
        <div className="mb-6">
          <ErrorNote>Could not check the ledger.</ErrorNote>
        </div>
      ) : null}

      {pendingAction && !actionBalance ? (
        <div className="mb-6">
          <InfoNote>
            <strong>{humanise(pendingAction)} — pick a lot.</strong> Choose a balance below and the{' '}
            {humanise(pendingAction).toLowerCase()} panel opens ready to post.
          </InfoNote>
        </div>
      ) : null}

      {showReceive ? (
        <ReceiveStockForm
          defaultItemId={receiveItemId}
          onDone={() => {
            setShowReceive(false);
            setReceiveItemId(undefined);
          }}
        />
      ) : null}
      {actionBalance ? (
        <StockActionPanel
          balance={actionBalance}
          initialMode={pendingAction ?? undefined}
          onDone={() => {
            setActionBalance(null);
            setPendingAction(null);
          }}
        />
      ) : null}
      {openLotId ? (
        <LotDetailPanel lotId={openLotId} onClose={() => setOpenLotId(null)} />
      ) : null}

      <Segmented
        label="Inventory view"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'balances', label: 'Balances' },
          { value: 'ledger', label: 'Ledger' },
        ]}
      />

      {tab === 'balances' ? (
        <Card>
          <CardBody>
            <FilterBar activeCount={search ? 1 : 0}>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <input
                  name="balanceSearch"
                  className={`${inputClass} max-w-xs`}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search item or lot"
                  aria-label="Search balances"
                />
                <label className="flex items-center gap-2 text-sm text-ink-body">
                  <input
                    name="hideEmpty"
                    type="checkbox"
                    checked={hideEmpty}
                    onChange={(event) => setHideEmpty(event.target.checked)}
                  />
                  Hide empty
                </label>
                <Tooltip
                  subject="Hide empty"
                  text="A lot that has been fully used up keeps its row so its history stays intact. Tick this to see only the lots that still have stock in them."
                />
                {balances.isSuccess ? (
                  <span className="text-sm text-ink-muted">
                    {balances.data.total} balance{balances.data.total === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
            </FilterBar>

            {balances.isPending ? <Spinner /> : null}
            <ErrorNote>
              {balances.isError ? 'Could not load balances. Is the API running?' : ''}
            </ErrorNote>

            {balances.isSuccess ? (
              <DataTable
                rows={balances.data.items}
                columns={balanceColumns}
                rowKey={(balance) => `${balance.lotId}:${balance.locationId}`}
                rowClassName={() => 'hover:bg-brand-soft/40'}
                actionsHeader="Actions"
                actions={balanceActions}
                empty={
                  search
                    ? `Nothing matches “${search}”.`
                    : 'No stock yet. Use “Receive stock” to record the first delivery.'
                }
              />
            ) : null}
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <FilterBar activeCount={movementType ? 1 : 0}>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <select
                  name="movementType"
                  className={`${inputClass} max-w-xs`}
                  value={movementType}
                  onChange={(event) => setMovementType(event.target.value)}
                  aria-label="Filter by movement type"
                >
                  <option value="">All movement types</option>
                  {MOVEMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {humanise(type)}
                    </option>
                  ))}
                </select>
                {movements.isSuccess ? (
                  <span className="text-sm text-ink-muted">
                    {movements.data.total} movement{movements.data.total === 1 ? '' : 's'}
                  </span>
                ) : null}
              </div>
            </FilterBar>

            {movements.isPending ? <Spinner /> : null}
            <ErrorNote>{movements.isError ? 'Could not load the ledger.' : ''}</ErrorNote>

            {movements.isSuccess ? (
              <DataTable
                rows={movements.data.items}
                columns={movementColumns}
                rowKey={(movement) => movement.id}
                rowClassName={(movement) => (movement.reversalOfId ? 'bg-accent-soft/40' : '')}
                actionsHeader={
                  <span className="inline-flex items-center gap-1.5">
                    Correct
                    <Tooltip
                      subject="Correcting a movement"
                      text="A movement is never edited or deleted — that is what makes the history trustworthy. Undoing one posts an opposite entry that points back at the original, so both stay on the record. If the stock it put here has since been used, the reversal is refused."
                    />
                  </span>
                }
                actions={movementActions}
                empty="No movements yet."
              />
            ) : null}
          </CardBody>
        </Card>
      )}

      {reversing ? (
        <Card className="mt-6 border-l-4 border-l-accent">
          <CardBody>
            <h2 className="text-base font-bold text-ink">
              Reverse {humanise(reversing.type)}{' '}
              <span className="font-mono text-sm font-normal text-ink-muted">{reversing.id}</span>
            </h2>
            <p className="mt-1 text-sm font-light text-ink-body">
              The ledger is append-only, so this posts the opposite entry rather than changing
              anything. The original row stays exactly as it is, with a pointer to its correction.
              {reversing.groupId
                ? ' Both halves of this transfer will be reversed together.'
                : ''}
            </p>

            <div className="mt-4 max-w-xl">
              <label className="block">
                <span className="mb-1 block text-sm font-bold text-ink">Reason</span>
                <input
                  name="reverseReason"
                  className={inputClass}
                  value={reverseReason}
                  onChange={(event) => setReverseReason(event.target.value)}
                  placeholder="Why is this movement being undone?"
                />
              </label>

              {reverse.error ? (
                <div className="mt-3">
                  <ErrorNote>
                    {reverse.error instanceof ApiError
                      ? reverse.error.message
                      : 'Could not reverse the movement.'}
                  </ErrorNote>
                </div>
              ) : null}

              <div className="mt-3 flex gap-2">
                <Button
                  tone="danger"
                  disabled={reverse.isPending || reverseReason.trim().length < 3}
                  onClick={() => reverse.mutate({ id: reversing.id, reason: reverseReason.trim() })}
                >
                  {reverse.isPending ? 'Reversing…' : 'Post reversal'}
                </Button>
                <Button tone="ghost" onClick={() => setReversing(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <StickyActionBar
        actions={[
          {
            label: 'Receive stock',
            onClick: openReceive,
            capability: 'inventory.manage',
            tone: 'primary',
          },
        ]}
        hidden={showReceive || Boolean(actionBalance) || Boolean(openLotId)}
      />
    </>
  );
}
